import { useSuspenseQuery } from "@apollo/client";
import { DataTable, LabelPill, PrimaryFullScreenSpinner } from "@repo/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { Suspense } from "react";
import { graphql } from "~/gql/client";
import type { EburyClientListRowFragment } from "~/gql/client/graphql";

export const Route = createFileRoute("/__auth/ebury/")({
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

graphql(`
  fragment EburyClientListRow on EburyClient {
    id
    clientName
    deactivatedAt
    businessEntity {
      id
      name
      organisation {
        id
        name
      }
    }
  }
`);

const clientsDocument = graphql(`
  query AdminEburyClientList {
    eburyClients {
      ...EburyClientListRow
    }
  }
`);

const columns: ColumnDef<EburyClientListRowFragment>[] = [
  {
    accessorKey: "clientName",
    header: "Client Name",
    enableSorting: false,
  },
  {
    accessorKey: "id",
    header: "Client ID",
    enableSorting: false,
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) =>
      row.original.deactivatedAt ? (
        <LabelPill variant="warning" label="Deactivated" />
      ) : (
        <LabelPill variant="success" label="Active" />
      ),
    enableSorting: false,
  },
  {
    id: "links",
    header: "Links",
    cell: ({ row }) => (
      <div className="flex gap-4">
        <Link
          to="/ebury_/$clientId/trades"
          params={{ clientId: row.original.id }}
          className="text-primary-interactive hover:text-primary-interactive"
        >
          Trades
        </Link>
        <Link
          to="/ebury_/$clientId/beneficiaries"
          params={{ clientId: row.original.id }}
          className="text-primary-interactive hover:text-primary-interactive"
        >
          Beneficiaries
        </Link>
      </div>
    ),
    enableSorting: false,
  },
  {
    id: "businessEntity",
    header: "Business Entity",
    cell: ({ row }) => {
      const { businessEntity } = row.original;
      if (!businessEntity) {
        return (
          <span className="text-secondary">Not linked to Primary entity</span>
        );
      }
      return (
        <Link
          to={`/organisations/${businessEntity.organisation?.id}/business-entities/${businessEntity.id}`}
          className="text-primary-interactive hover:text-primary-interactive"
        >
          {businessEntity.name} ({businessEntity.organisation?.name})
        </Link>
      );
    },
    enableSorting: false,
  },
];

function View() {
  const { data } = useSuspenseQuery(clientsDocument);

  const clients = data.eburyClients ?? [];

  return (
    <DataTable
      data={clients}
      columns={columns}
      className="overflow-hidden border"
    />
  );
}
